/**
 * First word of a display name ("Alex Admin" -> "Alex"); "" for null/empty.
 * The one first-name rule — greetings/prefills across surfaces share it.
 */
export function firstNameOf(name: string | null | undefined): string {
  return (name ?? "").trim().split(/\s+/)[0] ?? "";
}

/**
 * Returns up to 2 uppercase initials from a name (e.g. "John Doe" -> "JD").
 * Handles null/undefined and empty strings.
 */
export function getInitials(name: string | null | undefined): string {
  if (!name) return "?";
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

/** A Postgres DATE as it arrives over the wire: `YYYY-MM-DD`, no time, no zone. */
const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Parse a server date string for display.
 *
 * A date-only string is a CALENDAR date, not an instant — but `new Date("2026-01-20")`
 * reads it as UTC midnight, which `toLocaleDateString` then renders in local time: a day
 * EARLIER everywhere west of Greenwich ("Jan 19, 2026" across the Americas). Building it
 * at local midnight instead makes the day that comes out the day that went in.
 *
 * Anything with a time (TIMESTAMPTZ) is a real instant and is parsed as-is, so it still
 * renders in the admin's own zone — which is what you want for "when did this happen".
 */
function parseForDisplay(dateStr: string): { date: Date; dateOnly: boolean } {
  if (!DATE_ONLY_RE.test(dateStr)) return { date: new Date(dateStr), dateOnly: false };
  const [y, m, d] = dateStr.split("-").map(Number);
  return { date: new Date(y!, m! - 1, d!), dateOnly: true };
}

/**
 * Formats a date string as "Mon DD, YYYY". Returns "Never" for null/empty.
 */
export function formatDate(dateStr: string | null): string {
  if (!dateStr) return "Never";
  return parseForDisplay(dateStr).date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/**
 * Formats a date string as relative time ("Just now", "5m ago", "2h ago", "3d ago")
 * or falls back to formatDate for older dates.
 */
export function formatRelativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  // Future timestamps (a scheduled task's Next Run) used to fall through `mins < 1` and
  // read "Just now" for everything (2026-10-19, Nick). Same buckets, both directions.
  const future = diff < 0;
  const mins = Math.floor(Math.abs(diff) / 60000);
  if (mins < 1) return future ? "In under a minute" : "Just now";
  const unit =
    mins < 60
      ? `${mins}m`
      : mins < 60 * 24
        ? `${Math.floor(mins / 60)}h`
        : mins < 60 * 24 * 7
          ? `${Math.floor(mins / (60 * 24))}d`
          : null;
  if (unit === null) return formatDate(dateStr);
  return future ? `In ${unit}` : `${unit} ago`;
}

/**
 * Formats a date string with date and time (e.g. "Jan 1, 2025, 3:45 PM").
 */
export function formatDateTime(dateStr: string | null): string {
  if (!dateStr) return "Never";
  const { date, dateOnly } = parseForDisplay(dateStr);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    // A DATE has no time of day — appending "12:00 AM" to one invents precision the
    // column never had. Timestamps keep theirs.
    ...(dateOnly ? {} : { hour: "numeric" as const, minute: "2-digit" as const }),
  });
}

export { formatCode, formatDuration } from "@shared/format";
export { timeAgo } from "@shared/time-ago";

/**
 * Converts a duration in milliseconds to a human-readable string (e.g. "500ms", "1.2s", "3.5m").
 * Returns "—" for null.
 */
export function formatDurationMs(ms: number | null): string {
  if (ms === null) return "—";
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60_000).toFixed(1)}m`;
}
